import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, HelpCircle, Settings as SettingsIcon, ChevronRight, Headphones, X, Copy, Check, Loader2, Send } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useSubscription } from '../hooks/useApi';
import { AppLang } from '../i18n';
import { SupportChatPage } from './SupportChatPage';
import './SupportPage.css';
import { openTgLink } from '../lib/openTgLink';

interface SupportPageProps {
  user: any;
  lang: AppLang;
  subPage: string | null;
  onNavigate: (page: string) => void;
  onBack: () => void;
  onTabChange: (tab: any) => void;
}

export const SupportPage = ({ lang, subPage, onBack }: SupportPageProps) => {
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);
  const { data: subData, loading: subLoading } = useSubscription();
  const subUrl = subData?.subscription?.sub_url || '';
  // Зашифрованная happ://crypt5 ссылка (конфиг скрыт). Фолбэк — happ://add.
  const happLink = subData?.subscription?.happ_link || (subUrl ? `happ://add/${subUrl}` : '');

  const handleCopy = () => {
    if (!happLink) return;
    navigator.clipboard.writeText(happLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSend = () => {
    if (!happLink) return;
    const tg = (window as any).Telegram?.WebApp;
    const shareUrl = 'https://t.me/share/url?url=' + encodeURIComponent(happLink);
    if (tg?.openTelegramLink) tg.openTelegramLink(shareUrl);
    else window.open(shareUrl, '_blank');
  };

  if (subPage === 'chat') {
    return <SupportChatPage lang={lang} onBack={onBack} />;
  }

  return (
    <div className="sup">
      <motion.div className="sup-content" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        {/* Hero */}
        <div className="sup-hero">
          <div className="sup-hero-icon"><MessageCircle size={24} /></div>
          <h2 className="sup-hero-title">Связаться с поддержкой</h2>
          <p className="sup-hero-sub">
            Получите ответы на популярные вопросы или обратитесь к нам за помощью
          </p>
        </div>

        {/* Menu items */}
        <div className="sup-list">
          <button className="sup-list-item" onClick={() => setShowQr(true)}>
            <SettingsIcon size={20} className="sup-item-icon" />
            <div className="sup-item-info">
              <span className="sup-item-label">Установка на другом устройстве</span>
              <span className="sup-item-desc">QR-код вашей подписки</span>
            </div>
            <ChevronRight size={18} className="sup-item-arrow" />
          </button>

          <button className="sup-list-item" onClick={() => {
            const tg = (window as any).Telegram?.WebApp;
            const url = 'https://t.me/Patrik_vpn_support';
            openTgLink(url);
          }}>
            <Headphones size={20} className="sup-item-icon" />
            <div className="sup-item-info">
              <span className="sup-item-label">Поддержка</span>
              <span className="sup-item-desc">Написать в Telegram</span>
            </div>
            <ChevronRight size={18} className="sup-item-arrow" />
          </button>
        </div>
      </motion.div>

      {/* QR Modal */}
      <AnimatePresence>
        {showQr && (
          <motion.div className="sup-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowQr(false)}>
            <motion.div className="sup-modal" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }} onClick={e => e.stopPropagation()}>
              <div className="sup-modal-header">
                <span className="sup-modal-title">Подписка</span>
                <button className="sup-modal-close" onClick={() => setShowQr(false)}><X size={22} /></button>
              </div>
              <div className="sup-modal-body">
                {subLoading ? (
                  <div className="sup-qr-loading">
                    <Loader2 size={32} className="sup-qr-spinner" />
                    <p className="sup-qr-hint">Загрузка...</p>
                  </div>
                ) : !subUrl ? (
                  <p className="sup-qr-hint">Подписка не найдена. Оформите подписку, чтобы получить QR-код.</p>
                ) : (
                  <>
                    <div className="sup-qr-wrap">
                      <QRCodeSVG value={happLink} size={200} bgColor="transparent" fgColor="#ffffff" level="M" />
                    </div>
                    <p className="sup-qr-hint">
                      Отсканируйте QR-код камерой на другом устройстве, чтобы скопировать ссылку подписки
                    </p>
                    <div className="sup-qr-actions">
                      <button className="sup-qr-send" onClick={handleSend}>
                        <Send size={18} />
                        Отправить
                      </button>
                      <button className="sup-qr-copy" onClick={handleCopy}>
                        {copied ? <Check size={18} /> : <Copy size={18} />}
                        {copied ? 'Скопировано' : 'Скопировать'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
